import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { roleNameMap } from '../../constants/roles';
import SearchLocations from '../GoogleMaps/LocationSearch/SearchLocations';
import UserMenu from '../HeaderNav/UserMenu';
import { getUserMenuLinks } from '../HeaderNav/userMenuLinks';
import useLoginModal from '../../hooks/useLoginModal';
import { usePageContext } from '../../context/PageContext/PageContext';
import MobileNav from '../MobileNav/MobileNav';
import { Bars3Icon } from '@heroicons/react/24/solid';
import logoBadge from '../../assets/images/logos/park-pro-logo-stacked-badge.png';
import './AppHeader.css';

function getContextLabel(role) {
    return roleNameMap[role]?.shortName ?? 'ParkPro';
}

function SearchIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
    );
}

export default function AppHeader({
    role,
    variant = 'app',
    showHostNavLinks = false,
    showMobileMenu = false,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logOut, profile, isAuthLoading, LogOutModalComponent } = useAuth();
    const { showLoginModal, LoginModalComponent } = useLoginModal();
    const { mobileSidebarOpen, setMobileSidebarOpen, setShowDesktopSideNav } = usePageContext();

    const contextLabel = getContextLabel(role);
    // Signed-in users get the phone menu panel (MobileNav); signed-out
    // visitors keep the older slide-in menu (DashboardSideNav).
    const signedIn = Boolean(user && profile);
    const links = getUserMenuLinks(role, navigate, logOut);
    const logoPath = variant === 'app' && user ? '/' : '/find-parking';

    useEffect(() => {
        if (isAuthLoading === false && !user && location.pathname === '/login') {
            showLoginModal();
            setShowDesktopSideNav(false);
        } else if (user && location.pathname === '/login') {
            navigate('/');
        }
    }, [location, showLoginModal, isAuthLoading, user, navigate, setShowDesktopSideNav]);

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    const toggleMobileSidebar = () => {
        setMobileSidebarOpen(!mobileSidebarOpen);
    };

    // Below 576 px the search box sits in a row under the header, opened by a
    // search button. It starts open on the map page, where search is the
    // page's job, and closed elsewhere.
    const [searchOpen, setSearchOpen] = useState(location.pathname === '/find-parking');
    useEffect(() => {
        setSearchOpen(location.pathname === '/find-parking');
    }, [location.pathname]);

    const goLogo = () => navigate(logoPath);

    return (
        <>
            <header className="app-header">
                {showMobileMenu && (
                    <button
                        type="button"
                        className="app-header-menu-btn lg:hidden"
                        onClick={toggleMobileSidebar}
                        aria-expanded={Boolean(mobileSidebarOpen)}
                        aria-haspopup={signedIn ? 'dialog' : undefined}
                    >
                        <Bars3Icon aria-hidden="true" />
                        <span className="app-header-menu-label">Menu</span>
                    </button>
                )}
                <img
                    className="app-header-logo"
                    src={logoBadge}
                    alt="ParkPro"
                    onClick={goLogo}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goLogo();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                />
                <div className="app-header-tagline">
                    Only ParkPro is driven by real-time driver demand.
                </div>
                {role && (
                    <div className="app-header-context">{contextLabel}</div>
                )}
                {showHostNavLinks && (
                    <nav className="app-header-nav" aria-label="Main">
                        <button
                            type="button"
                            className={`app-header-nav-link${isActive('/') ? ' active' : ''}`}
                            onClick={() => navigate('/')}
                        >
                            Home
                        </button>
                        <button
                            type="button"
                            className={`app-header-nav-link${isActive('/dashboard/my-properties') ? ' active' : ''}`}
                            onClick={() => navigate('/dashboard/my-properties')}
                        >
                            My Properties
                        </button>
                        <button
                            type="button"
                            className={`app-header-nav-link${isActive('/dashboard/listings/bulk') ? ' active' : ''}`}
                            onClick={() => navigate('/dashboard/listings/bulk')}
                        >
                            Bulk Upload
                        </button>
                    </nav>
                )}
                {user && (
                    <div
                        id="app-header-search"
                        className={`app-header-search${searchOpen ? ' is-open' : ''}`}
                    >
                        <SearchIcon />
                        <SearchLocations
                            searchInputPlaceholder="Search destinations"
                            searchInputFillContainer
                        />
                    </div>
                )}
                <div className="app-header-right">
                    {user && (
                        <button
                            type="button"
                            className="app-header-search-toggle"
                            onClick={() => setSearchOpen((open) => !open)}
                            aria-expanded={searchOpen}
                            aria-controls="app-header-search"
                            aria-label="Search destinations"
                        >
                            <SearchIcon />
                        </button>
                    )}
                    {user ? (
                        <UserMenu
                            user={user}
                            profile={profile}
                            links={links}
                            variant="app"
                        />
                    ) : (
                        <button
                            type="button"
                            className="app-header-sign-in"
                            onClick={showLoginModal}
                        >
                            Sign in
                        </button>
                    )}
                </div>
            </header>
            {showMobileMenu && signedIn && <MobileNav role={role} />}
            <LoginModalComponent />
            <LogOutModalComponent />
        </>
    );
}
